import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpEvent } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Env } from '@usimweb/core/models';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly AuthEndpoint = AuthService.ENV.USIM_HOST + '/api/appstore_auth/';
  private readonly RefreshEndpoint = AuthService.ENV.USIM_HOST + '/api/token/refresh/';

  private helper: JwtHelperService = new JwtHelperService();
  private token: string = '';
  private refresh: string = '';

  public static ENV: Env = {} as Env;

  // for development
  public static userName = 'Dennis Skopp';
  public static basfUserName = 'skoppdp';

  public static isWriter = false;
  public static isFitting = false;

  public cookieCallback = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject('environment') private environment: Env,
    private httpClient: HttpClient,
    private cookieService: CookieService
  ) {
    AuthService.ENV = this.environment;
  }

  public get accessToken(): string {
    return this.cookieService.get('access_token');
  }

  public get version(): string {
    return this.cookieService.get('version');
  }

  public get allCookies(): { [key: string]: string } {
    return this.cookieService.getAll();
  }

  public initializationRequest(): Observable<any> {
    const body = {
      HTTP_COOKIE: this.buildCookieString(),
    };

    return this.httpClient
      .post(this.environment.USIM_HOST + '/api/appstore_auth/', body, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      })
      .pipe(
        map((result: any) => {
          this.token = result.auth.access;
          this.refresh = result.auth.refresh;
          const accessToken = result.auth.access;
          const expireTime = this.helper.getTokenExpirationDate(accessToken);
          const decodedToken = this.helper.decodeToken(accessToken);
          const now = new Date();
          const expireTimeWriter = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());

          if (result.foamwriter && result.foamfitting) {
            AuthService.isWriter = result.foamwriter;
            AuthService.isFitting = result.foamfitting;
          }

          if (this.environment.production) {
            this.cookieService.set('access_token', accessToken, expireTime || 0, undefined, undefined, true, 'Strict');
            this.cookieService.set('currentUser', JSON.stringify(decodedToken), expireTime || 0, undefined, undefined, true, 'Strict');

            if (result.foamwriter && result.foamfitting) {
              this.cookieService.set('foamwriter', String(AuthService.isWriter), expireTimeWriter, undefined, undefined, true, 'Strict');
              this.cookieService.set('foamfitting', String(AuthService.isFitting), expireTimeWriter, undefined, undefined, true, 'Strict');
            }
          } else {
            if (this.environment.TOKEN.includes('access_token')) {
              // Do something if the string contains "access_token"
              console.log('String contains access_token');
              this.cookieService.set('access_token', accessToken, expireTime || 0);
            } else {
              // Do something if the string does not contain "access_token"
              console.log('String does not contain access_token');
              this.cookieService.set('access_token', this.environment.TOKEN, expireTime || 0);
            }

            if (result.foamwriter && result.foamfitting) {
              this.cookieService.set('foamwriter', String(AuthService.isWriter), expireTimeWriter);
              this.cookieService.set('foamfitting', String(AuthService.isFitting), expireTimeWriter);
            }

            this.cookieService.set('currentUser', JSON.stringify(decodedToken), expireTime || 0);
          }

          this.cookieCallback.next(true);
        })
      );
  }

  public refreshRequest(): Observable<any> {
    const body = {
      refresh: encodeURIComponent(this.refresh),
    };
    return this.httpClient
      .post(this.RefreshEndpoint, body, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      })
      .pipe(
        map((result: any) => {
          const dict = result;
          this.token = dict.access;
        })
      );
  }

  public refreshToken(): Observable<HttpEvent<any>> {
    if (!this.token) {
      return this.initializationRequest();
    } else {
      return this.refreshRequest();
    }
  }

  public logout() {
    this.cookieService.delete('access_token', '/');
    this.cookieService.delete('refresh_token', '/');
    this.cookieService.delete('currentUser', '/');
    this.forwardToLogin();
  }

  public forwardToLogin() {
    const loginUrl = 'https://app.roqs.basf.net/auth/login.html?redirect_uri=' + window.location.href;
    window.location.replace(loginUrl);
  }

  public setUserName() {
    if (this.environment.production) {
      const cookies = this.cookieService.getAll() as any;
      AuthService.userName = cookies.basf_federation_given_name + ' ' + cookies.basf_federation_sn;
      AuthService.basfUserName = cookies.basf_federation_cn_last_logged_in;
    }
  }

  public setCookies() {
    if (this.environment.production) {
      const cookies = this.cookieService.getAll() as any;
      AuthService.userName = cookies.basf_federation_given_name + ' ' + cookies.basf_federation_sn;
      AuthService.basfUserName = cookies.basf_federation_cn_last_logged_in;
    }
  }

  public getWriterCookie(): boolean {
    try {
      AuthService.isWriter = JSON.parse(this.cookieService.get('foamwriter').toLowerCase());
      return JSON.parse(this.cookieService.get('foamwriter').toLowerCase());
    } catch (error) {
      AuthService.isWriter = false;
      return false;
    }
  }

  public getFittingCookie(): boolean {
    try {
      AuthService.isFitting = JSON.parse(this.cookieService.get('foamfitting').toLowerCase());
      return JSON.parse(this.cookieService.get('foamfitting').toLowerCase());
    } catch (error) {
      AuthService.isFitting = false;
      return false;
    }
  }

  private buildCookieString() {
    if (this.environment.production) {
      const cookies = this.cookieService.getAll() as any;

      AuthService.userName = cookies.basf_federation_given_name + ' ' + cookies.basf_federation_sn;
      AuthService.basfUserName = cookies.basf_federation_cn_last_logged_in;

      let cookieString = '';
      for (const key of Object.keys(cookies)) {
        if (key !== 'version') {
          cookieString += encodeURIComponent(key) + '=' + encodeURIComponent(cookies[key]) + '; ';
        }
      }
      if (cookieString.endsWith('; ')) {
        cookieString = cookieString.slice(0, cookieString.length - 2);
      }
      return cookieString;
    }

    return this.environment.TOKEN;
  }
}
